import { createContext, useContext, type ReactNode } from 'react';

import { RootStore, rootStore } from './RootStore';

/**
 * React context for the MobX root store.
 */
const StoreContext = createContext<RootStore | null>(null);

/**
 * Props for the StoreProvider component.
 */
interface StoreProviderProps {
  children: ReactNode;
  store?: RootStore;
}

/**
 * Provider component that makes the MobX stores available to all children.
 *
 * @example
 * ```tsx
 * <StoreProvider>
 *   <App />
 * </StoreProvider>
 * ```
 */
export function StoreProvider({ children, store = rootStore }: StoreProviderProps): ReactNode {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

/**
 * Hook to access the root store from any component.
 *
 * @returns The root store instance
 * @throws Error if used outside of StoreProvider
 *
 * @example
 * ```tsx
 * const Component = observer(function Component() {
 *   const { appStore } = useStore();
 *   return <div>{appStore.isConnected ? 'Connected' : 'Disconnected'}</div>;
 * });
 * ```
 */
export function useStore(): RootStore {
  const store = useContext(StoreContext);

  if (!store) {
    throw new Error('useStore must be used within a StoreProvider');
  }

  return store;
}

/**
 * Hook to access just the app store.
 *
 * @returns The app store instance
 */
export function useAppStore(): RootStore['appStore'] {
  const { appStore } = useStore();
  return appStore;
}
